<script setup lang="ts">
import { EMIT } from '@/enum';
import { MenuItem } from 'primevue/menuitem';
import TabMenu, { TabMenuChangeEvent } from 'primevue/tabmenu';
import { ref, watch, onUnmounted, onMounted, computed } from 'vue';
import { useRoute } from 'vue-router';

interface TabMenuProps {
  items: MenuItem[];
  activeIndex?: number;
  className?: string;
  attendeeFilter?: number;
  activeRoute?: string;
}

const route = useRoute();

const tabMenuProps = withDefaults(defineProps<TabMenuProps>(), {
  items: () => {
    return [{}] as MenuItem[];
  },
  className: 'tab-menu-container',
  activeIndex: 0,
  attendeeFilter: 0,
  activeRoute: undefined,
});

const emit = defineEmits<{
  (e: EMIT.TAB, value: number): void;
}>();

const itemsAreRoutes = computed<boolean>(() => tabMenuProps.items.every((item) => item?.route));

const activeTabIndex = computed(() => {
  if (tabMenuProps.activeRoute) {
    return tabMenuProps.items.findIndex((item) => item.route === tabMenuProps.activeRoute);
  }
  if (itemsAreRoutes.value) {
    return tabMenuProps.items.findIndex((item) => item.route === route.path);
  }
  return tabMenuProps.activeIndex;
});

const aiRef = ref(activeTabIndex.value);

watch(activeTabIndex, (newValue) => {
  aiRef.value = newValue >= 0 ? newValue : 0;
});

onMounted(() => {
  window.addEventListener('popstate', setFocusOnTabItem);
  aiRef.value = activeTabIndex.value;
});

onUnmounted(() => {
  window.removeEventListener('popstate', setFocusOnTabItem);
});

const setFocusOnTabItem = () => {
  const parent = document.getElementsByClassName('p-tabmenuitem p-highlight')[0];
  if (parent) {
    const target = parent.getElementsByClassName('p-menuitem-link')[0];
    if (target) (target as HTMLElement).focus();
  }
};

const tabChange = (e: TabMenuChangeEvent) => {
  setFocusOnTabItem();
  emit(EMIT.TAB, e.index);
};
</script>

<template>
  <div>
    <Skeleton v-if="!items || !items.length" height="36px" width="530px" border-radius="6" />
    <div v-if="items.length" :class="className">
      <TabMenu v-if="itemsAreRoutes" v-model:active-index="aiRef" :model="items" @tab-change="tabChange">
        <!-- @vue-skip -->
        <template #item="{ item, props }">
          <router-link v-if="item.route" v-slot="{ href, navigate }" :to="item.route" custom>
            <a :href="href" v-bind="props.action" @click="navigate">
              <span v-bind="props.label">{{ item.label }}</span>
            </a>
          </router-link>
        </template>
      </TabMenu>
      <TabMenu v-else v-model:active-index="aiRef" :model="items" @tab-change="tabChange" />
      <div class="tab-menu-details"></div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.tab-menu-container {
  height: 36px;
  width: fit-content;
  position: relative;

  &::before {
    content: '';
    background: var(--primary-color-medium);
    border-radius: 0.5rem;
    position: absolute;
    top: 0px;
    left: 0px;
    height: 36px;
    width: 100%;
  }

  .tab-menu-details {
    position: absolute;
    top: 0px;
    left: 0px;
    width: 100%;
    height: 32px;
    border-radius: 0.5rem;
    transition: 0.12s ease-in-out;
    box-shadow: inset 0 2px 2px var(--primary-200), inset 0px -2px 2px var(--primary-500);
    pointer-events: none;
  }

  :deep(.p-tabmenu) {
    height: 2rem;
    background: white;
    width: fit-content;
    border: 4px solid var(--primary-color-light);
    border-radius: 0.5rem;
    position: relative;
    overflow-x: hidden;

    .p-tabmenu-nav {
      background: none;
      border: none;
      border-width: 0px;
      height: 100%;
      margin: 0rem -0.125rem;

      .p-tabmenuitem {
        height: 100%;

        .p-menuitem-link {
          background: none;
          border: none;
          border-width: 0px;
          border-radius: 0.25rem;
          height: 100%;
          padding: 0rem 0.5rem;
          margin: 0rem 0.125rem;
          text-decoration: none !important;
          transition: 0.2s ease;

          .p-menuitem-text {
            color: var(--text-color-secondary);
            transition: 0.2s ease;
            white-space: nowrap;
          }

          &:focus {
            box-shadow: inset 0 0 0 0.2rem var(--primary-color-light);
          }
        }

        .p-menuitem-link:not(.p-disabled):focus {
          outline: 0 none;
          outline-offset: 0;
        }

        &.p-highlight .p-menuitem-link {
          background: var(--primary-color-extra-light);
          outline: 0 none;
          outline-offset: 0;
          box-shadow: inset 0 0 0 0.2rem var(--primary-color-extra-light);

          .p-menuitem-text {
            color: var(--primary-color-dark);
          }

          &:focus {
            box-shadow: inset 0 0 0 0.2rem var(--primary-color-extra-light);
          }
        }

        &:hover {
          .p-menuitem-link,
          .p-menuitem-text {
            color: var(--primary-color-dark) !important;
          }
        }
      }
    }
  }
}
</style>
